import { startCase } from 'lodash';
import { ReactNode } from 'react';
import { Box, Button, Flex, Spinner, ThemeUIStyleObject } from 'theme-ui';
import {
  AuditLog,
  ItemTag,
  Maybe,
  PageInfo,
  Warehouse,
} from '../../graphql/types/generated';
import { formatMoney, priceDecimal } from '../../utils/PricingIO';
import { predictableUUID } from '../../utils/predictableUuid';
import { LocaleUpdatedAt } from '../LocaleUpdatedAt';

/**
 * The general cell renderer that tries to guess what to render based on a field
 * and type mapping.
 * This falls back to string rendering.
 * @param fields The fields mapping that specify how to render specific fields
 * @param types Specifies how to render a given field type
 * @param field The field to render on the AuditLog.
 */
export function createAuditCellRenderer(
  fields: AuditLogFields,
  types: AuditLogTypes,
  field: 'newValue' | 'oldValue',
) {
  return (row: AuditLog): ReactNode => {
    const value = JSON.parse(row[field]);
    const renderer = fields[baseAuditField(row.field)]?.value;

    if (value === null || value === undefined) return <Null />;
    else if (typeof renderer === 'function') return renderer(value, row);
    else if (renderer && renderer in types) {
      return types[renderer](value, row);
    } else if (value instanceof Date) {
      return types.date(value, row);
    } else if (typeof value === 'boolean') {
      return types.boolean(value, row);
    } else if (typeof value === 'number') {
      return types[Math.floor(value) === value ? 'integer' : 'float'](
        value,
        row,
      );
    }

    return types.string(value, row);
  };
}

const nullStyle = {
  color: 'secondaryText',
  fontStyle: 'italic',
  fontWeight: 'light',
  opacity: 0.5,
  width: '100%',
};

export function Null() {
  return (
    <Box sx={nullStyle} title="Not set">
      &nbsp;
    </Box>
  );
}

/**
 * Render an attempt to map enums to the specified values.
 * If not found, it will render a formatted version of the value.
 */
export function createEnumRenderer(enumeration: { [key: string]: string }) {
  return (value: unknown): ReactNode => {
    return enumeration[value as string] ?? startCase(value as string);
  };
}

/**
 * Render an attempt to map Vendor UUIDs to it's names.
 * If not found, it will render the UUID.
 * @param list The objects that are used to map UUIDs to names.
 * @param id The id field
 * @param name The name field
 */
export function createIdLookupRenderer<T, U extends keyof T>(
  list: Maybe<T>[],
  id: U,
  name: U,
) {
  const byId = new Map<unknown, T[U]>(
    (list ?? [])
      .filter((o): o is T => o !== null && o !== undefined)
      .map((o) => [o[id], o[name]]),
  );
  const one = (value: unknown): string => (byId.get(value) ?? value) as string;
  return (value: unknown): ReactNode =>
    Array.isArray(value) ? value.map(one).join(', ') : one(value);
}

/** `upccode.0` -> `upccode`: indexed rows share their base field's label and renderer. */
export function baseAuditField(field: string): string {
  return field.replace(/\.\d+$/, '');
}

/**
 * Drops a `*_amountInCents` row when the same write also logged its `*_amount` twin. Both rows
 * from one write share one `Date` instance (`AuditLog.initBy`), so comparing `date` in full finds
 * an exact match with no truncation to reason about.
 */
export function hideDuplicateAmountRows(logs: AuditLog[]): AuditLog[] {
  const key = (log: AuditLog) =>
    `${log.objectId}|${log.date}|${log.field.replace(/InCents$/, '')}`;
  const amounts = new Set(
    logs.filter((l) => l.field.endsWith('_amount')).map(key),
  );
  return logs.filter(
    (l) => !l.field.endsWith('_amountInCents') || !amounts.has(key(l)),
  );
}

/**
 * Create a label renderer for a field
 * @param label The field label before the tag prefix
 * @param mapping
 */
export function createTagLabel(
  label: string,
  mapping: { [key: string]: string },
) {
  return (_: unknown, row: AuditLog): ReactNode => {
    const tag: string =
      row.objectId in mapping ? mapping[row.objectId] : row.objectId;
    return `${label} (${tag})`;
  };
}

/**
 * Create a audit log objectId mapping of the computed(itemId, tag.id) -> name.
 * @param itemId The item id
 * @param tags A list of tag objects containing ids and names.
 */
export function createTagObjectIdMap(
  itemId: string,
  tags: Maybe<ItemTag[]>,
): Record<string, string> {
  const itemTags: Record<string, string> = {};
  for (const tag of tags || []) {
    itemTags[itemTagAuditObjectId(itemId, tag.id)] = tag.name;
  }

  return itemTags;
}

/**
 * Generate the tag audit log objectId for composite object
 * @param itemId The item's id for the association
 * @param warehouseId The tag id for the association
 */
export function itemTagAuditObjectId(itemId: string, tagId: string): string {
  return predictableUUID(`${itemId}:${tagId}`);
}

/**
 * A field diffed element-by-element (e.g. `fulfillmentWarehouseIds.2`) shares its base field's
 * label, which otherwise makes every element read as the same row. Suffix with the element index
 * so simultaneous changes to more than one element are still distinguishable.
 */
function indexSuffix(field: string): string {
  const base = baseAuditField(field);
  return field.length > base.length ? ` #${field.slice(base.length + 1)}` : '';
}

/**
 * Create a label renderer for a field
 * @param label The field label before the warehouse prefix
 * @param mapping
 */
export function createUpcCodeLabel(label: string) {
  return (_: unknown, row: AuditLog): ReactNode => {
    return (
      (row.objectType === 'sample' ? `Sample ${label}` : label) +
      indexSuffix(row.field)
    );
  };
}

/**
 * Create a label renderer for a field
 * @param label The field label before the warehouse prefix
 * @param mapping
 */
export function createWarehouseLabel(
  label: string,
  mapping: { [key: string]: string },
) {
  return (_: unknown, row: AuditLog): ReactNode => {
    const warehouse: string =
      row.objectId in mapping ? mapping[row.objectId] : row.objectId;
    return `${label}${indexSuffix(row.field)} (${warehouse})`;
  };
}

/**
 * Create a audit log objectId mapping of the computed(itemId, wa.id) -> name.
 * @param itemId The item id
 * @param warehouses A list of warehouse objects containing ids and names.
 */
export function createWarehouseObjectIdMap(
  itemId: string,
  warehouses: Maybe<Warehouse[]>,
): Record<string, string> {
  const waNames: Record<string, string> = {};
  for (const assoc of warehouses || []) {
    waNames[itemWarehouseAuditObjectId(itemId, assoc.id)] = assoc.name;
  }
  return waNames;
}

/** Default type renderers that are used with the AuditLogPane fields parameter */
export const defaultRenders: AuditLogTypes = {
  boolean: (value: unknown) => (value ? 'Yes' : 'No'),
  date: (value: unknown) => <LocaleUpdatedAt value={value as string} />,
  float: (value: unknown) => {
    if (isNaN(value as number)) return <Null />;
    return Number(value).toLocaleString('en-us', { maximumFractionDigits: 5 });
  },
  integer: (value: unknown) => {
    return !isNaN(Number(value)) ? Math.floor(Number(value)) : <Null />;
  },
  money: (value: unknown) => {
    const amount = Number(value);
    if (isNaN(amount)) return <Null />;
    return formatMoney(priceDecimal(null, { amount }));
  },
  money_cents: (value: unknown) => {
    const amount = Number(value) / 100;
    if (isNaN(amount)) return <Null />;
    return formatMoney(priceDecimal(null, { amount }));
  },
  string: (value: unknown) => {
    return (
      <Box sx={valueCellStyle} title={String(value)}>
        {String(value)}
      </Box>
    );
  },
};

/**
 * Generate the warehouse audit log objectId for composite object
 * @param itemId The item's id for the association
 * @param warehouseId The warehouse id for the association
 */
export function itemWarehouseAuditObjectId(
  itemId: string,
  warehouseId: string,
): string {
  return predictableUUID(`${itemId}:${warehouseId}`);
}

const valueCellStyle: ThemeUIStyleObject = {
  maxWidth: '25vw',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** "<name> || <email> || <id>", with a caller-supplied fallback for a null (system) user. */
export function formatAuditUser(
  user:
    | Maybe<{ email?: Maybe<string>; id: string; name?: Maybe<string> }>
    | undefined,
  fallback?: string,
): string | undefined {
  return user?.name || user?.email || user?.id || fallback;
}

export const PAGE_SIZE = 20;
export const defaultCursor: NavigationCursor = { first: PAGE_SIZE };
export const defaultPageInfo: PageInfo = {
  hasPreviousPage: false,
  hasNextPage: false,
};

/** GraphQL sometimes omits `startCursor` on an otherwise-valid page; a Previous click needs a real value to send back. */
export function normalizePageInfo(pageInfo: PageInfo): PageInfo {
  return pageInfo.startCursor ? pageInfo : { ...pageInfo, startCursor: '' };
}

/** Shown above a paged list instead of a query failure rendering as a false "no results". */
export function QueryErrorNotice() {
  return (
    <Box sx={{ color: 'errorDefault', paddingX: 3, paddingY: 2 }}>
      Couldn&rsquo;t load this history. Try again.
    </Box>
  );
}

/** The Previous/Next toolbar shared by every cursor-paged audit-log list. */
export function PaginationControls({
  fetching,
  pageInfo,
  onPrevious,
  onNext,
}: {
  fetching: boolean;
  pageInfo: PageInfo;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        gap: 2,
        justifyContent: 'flex-end',
        marginBottom: 2,
      }}
    >
      {fetching && <Spinner sx={{ height: '24px' }} />}
      <Button
        disabled={fetching || !pageInfo.hasPreviousPage}
        variant="primary"
        onClick={onPrevious}
      >
        Previous
      </Button>
      <Button
        disabled={fetching || !pageInfo.hasNextPage}
        variant="primary"
        onClick={onNext}
      >
        Next
      </Button>
    </Flex>
  );
}

export type NavigationCursor = {
  after?: PageInfo['endCursor'];
  first?: number;
  before?: PageInfo['startCursor'];
  last?: number;
};

export type AuditLogFields = {
  [key: string]: {
    label?: string | AuditLogRenderer;
    value?: string | AuditLogRenderer;
  };
};
export type AuditLogRenderer = (value: unknown, row: AuditLog) => ReactNode;
export type AuditLogTypes = { [key: string]: AuditLogRenderer };
